import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Typography, Paper, Button, TextField, Chip, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow,
  Grid, Divider, Drawer, IconButton, Snackbar, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
  FormControlLabel, Avatar, Fab,
} from '@mui/material';
import {
  Sync as SyncIcon, Search as SearchIcon, Close as CloseIcon,
  OpenInNew as OpenInNewIcon, Email as EmailIcon, Forum as ForumIcon,
  ConfirmationNumber as TicketIcon, CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon, Refresh as RefreshIcon,
  Lock as LockIcon, LockOpen as LockOpenIcon,
  PersonAdd as AssignIcon, Code as DevIcon, HeadsetMic as SupportIcon,
  AutoAwesome as AiIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import nablSupportService from '../../services/nablSupportService';

const STATUS_COLOR = { Open: 'error', Closed: 'success', 'On Hold': 'warning' };
const AVATAR_COLORS = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c62828', '#00838f'];

// Returns { bg, border, label, chipColor } based on ticket age in days
const getAgePriority = (createdTime) => {
  if (!createdTime) return { bg: '#fff', border: '#bdbdbd', label: '?', chipColor: 'default' };
  const days = (Date.now() - new Date(createdTime).getTime()) / 86400000;
  if (days >= 7)  return { bg: '#ffebee', border: '#d32f2f', label: `${Math.floor(days)}d`, chipColor: 'error' };
  if (days >= 3)  return { bg: '#fff3e0', border: '#ed6c02', label: `${Math.floor(days)}d`, chipColor: 'warning' };
  if (days >= 1)  return { bg: '#fffde7', border: '#f9a825', label: `${Math.floor(days)}d`, chipColor: 'default' };
  const hrs = Math.floor(days * 24);
  return { bg: '#e8f5e9', border: '#2e7d32', label: `${hrs}h`, chipColor: 'success' };
};

const StatCard = ({ label, value, icon, color }) => (
  <Paper elevation={3} sx={{ p: 3, borderRadius: 2, borderLeft: `5px solid ${color}`, height: '100%' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ bgcolor: color, borderRadius: '50%', p: 1.5, display: 'flex', color: '#fff' }}>{icon}</Box>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>{value ?? '—'}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{label}</Typography>
      </Box>
    </Box>
  </Paper>
);

// Compact 6-column table — no horizontal scroll
const TicketTable = ({
  title, headerColor, tickets, loading, total, page, totalPages, pageSize,
  onPageChange, onRowClick, onStatusAction, onTypeAction, statusActionLoading,
  typeActionLoading, assignmentsMap, onAssign,
}) => (
  <Paper elevation={2} sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
    {/* Header */}
    <Box sx={{ px: 2, py: 1.25, borderRadius: '8px 8px 0 0', borderBottom: `2px solid ${headerColor}`, bgcolor: headerColor + '14' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: headerColor }}>
        {title}&nbsp;<Chip label={total} size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />
      </Typography>
    </Box>

    {loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, flexGrow: 1 }}><CircularProgress size={28} /></Box>
    ) : (
      <>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 500 }}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                <TableCell sx={{ fontWeight: 700, py: 0.75, width: '4%', fontSize: '0.7rem' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 0.75, width: '32%', fontSize: '0.7rem' }}>Ticket / Subject</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 0.75, width: '24%', fontSize: '0.7rem' }}>From / Date</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 0.75, width: '14%', fontSize: '0.7rem' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 0.75, width: '12%', fontSize: '0.7rem' }}>Assigned</TableCell>
                <TableCell sx={{ fontWeight: 700, py: 0.75, width: '14%', fontSize: '0.7rem', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((t, idx) => {
                const assigned = assignmentsMap[t.ticketNumber] || [];
                const isOpen = t.status === 'Open' || t.status === 'On Hold';
                const age = getAgePriority(t.createdTime);
                return (
                  <TableRow
                    key={t.ticketNumber}
                    hover
                    onClick={() => onRowClick(t)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: age.bg,
                      borderLeft: `4px solid ${age.border}`,
                      '&:hover': { filter: 'brightness(0.96)' },
                    }}
                  >
                    {/* Serial + queue position */}
                    <TableCell sx={{ py: 0.75, color: 'text.disabled', fontSize: '0.7rem', fontWeight: 700 }}>
                      {(page - 1) * pageSize + idx + 1}
                    </TableCell>

                    {/* Ticket # + Subject + age badge */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.2 }}>
                        <Typography sx={{ fontWeight: 700, color: '#1976d2', fontSize: '0.72rem' }}>
                          {t.ticketNumber}
                        </Typography>
                        <Chip
                          label={age.label}
                          size="small"
                          color={age.chipColor}
                          sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700 }}
                        />
                      </Box>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.subject}>
                        {t.subject || '—'}
                      </Typography>
                    </TableCell>

                    {/* Email + Date */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.email || t.fromEmailAddress}>
                        {t.email || t.fromEmailAddress || '—'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                        {t.createdTime ? new Date(t.createdTime).toLocaleDateString('en-IN') : ''}
                      </Typography>
                    </TableCell>

                    {/* Status chip + thread count */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Chip
                        label={t.status || '?'}
                        size="small"
                        color={STATUS_COLOR[t.status] || 'default'}
                        sx={{ height: 18, fontSize: '0.62rem', mb: 0.25 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <ForumIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                        <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>{t.threadCount || 0}</Typography>
                      </Box>
                    </TableCell>

                    {/* Assigned avatars */}
                    <TableCell sx={{ py: 0.75 }}>
                      <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                        {assigned.length === 0
                          ? <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>—</Typography>
                          : assigned.map((u, i) => (
                            <Tooltip key={u.user_id} title={u.name}>
                              <Avatar sx={{ width: 20, height: 20, fontSize: '0.58rem', bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                                {u.name[0].toUpperCase()}
                              </Avatar>
                            </Tooltip>
                          ))}
                      </Box>
                    </TableCell>

                    {/* 4 icon-button actions */}
                    <TableCell sx={{ py: 0.75 }} onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                        {t.webUrl && (
                          <Tooltip title="Open in Zoho">
                            <IconButton size="small" sx={{ p: 0.4 }} onClick={() => window.open(t.webUrl, '_blank')}>
                              <OpenInNewIcon sx={{ fontSize: 15, color: '#888' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Assign users">
                          <IconButton size="small" sx={{ p: 0.4, color: '#1976d2' }} onClick={() => onAssign(t)}>
                            <AssignIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.ticket_type === 'Development' ? 'Move to Support' : 'Mark as Development'}>
                          <span>
                            <IconButton
                              size="small"
                              sx={{ p: 0.4, color: t.ticket_type === 'Development' ? '#ed6c02' : '#7b1fa2' }}
                              disabled={typeActionLoading === t.ticketNumber}
                              onClick={() => onTypeAction(t.ticketNumber, t.ticket_type === 'Development' ? 'Support' : 'Development')}
                            >
                              {typeActionLoading === t.ticketNumber
                                ? <CircularProgress size={13} />
                                : t.ticket_type === 'Development'
                                  ? <SupportIcon sx={{ fontSize: 15 }} />
                                  : <DevIcon sx={{ fontSize: 15 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isOpen ? 'Close ticket' : 'Reopen ticket'}>
                          <span>
                            <IconButton
                              size="small"
                              sx={{ p: 0.4, color: isOpen ? '#2e7d32' : '#d32f2f' }}
                              disabled={statusActionLoading === t.ticketNumber}
                              onClick={() => onStatusAction(t.ticketNumber, isOpen ? 'Closed' : 'Open')}
                            >
                              {statusActionLoading === t.ticketNumber
                                ? <CircularProgress size={13} />
                                : isOpen
                                  ? <LockIcon sx={{ fontSize: 15 }} />
                                  : <LockOpenIcon sx={{ fontSize: 15 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!tickets.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No tickets found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1.5, py: 0.75, borderTop: '1px solid #f0f0f0' }}>
          <Button size="small" disabled={page === 1} onClick={() => onPageChange(page - 1)} sx={{ minWidth: 0, px: 1, fontSize: '0.72rem' }}>‹ Prev</Button>
          <Typography variant="caption" color="text.secondary">Page {page} / {totalPages || 1}</Typography>
          <Button size="small" disabled={page >= (totalPages || 1)} onClick={() => onPageChange(page + 1)} sx={{ minWidth: 0, px: 1, fontSize: '0.72rem' }}>Next ›</Button>
        </Box>
      </>
    )}
  </Paper>
);

export default function NablSupportDashboard() {
  const [stats, setStats] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [statusActionLoading, setStatusActionLoading] = useState(null);
  const [typeActionLoading, setTypeActionLoading] = useState(null);

  const [devTickets, setDevTickets] = useState([]);
  const [devTotal, setDevTotal] = useState(0);
  const [devTotalPages, setDevTotalPages] = useState(1);
  const [devPage, setDevPage] = useState(1);
  const [devLoading, setDevLoading] = useState(false);

  const [supTickets, setSupTickets] = useState([]);
  const [supTotal, setSupTotal] = useState(0);
  const [supTotalPages, setSupTotalPages] = useState(1);
  const [supPage, setSupPage] = useState(1);
  const [supLoading, setSupLoading] = useState(false);

  const [closedTickets, setClosedTickets] = useState([]);
  const [closedTotal, setClosedTotal] = useState(0);
  const [closedTotalPages, setClosedTotalPages] = useState(1);
  const [closedPage, setClosedPage] = useState(1);
  const [closedLoading, setClosedLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [pageSize] = useState(20);

  const [allUsers, setAllUsers] = useState([]);
  const [assignmentsMap, setAssignmentsMap] = useState({});
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [assigning, setAssigning] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (msg, sev = 'info') => setSnackbar({ open: true, message: msg, severity: sev });

  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const r = await nablSupportService.exportTickets();
      if (!r.data?.success) { showSnackbar('Export failed', 'error'); return; }
      const rows = r.data.data || [];
      const ws = XLSX.utils.json_to_sheet(rows);
      const colWidths = [
        { wch: 4 }, { wch: 14 }, { wch: 50 }, { wch: 30 }, { wch: 10 },
        { wch: 12 }, { wch: 20 }, { wch: 10 }, { wch: 12 }, { wch: 25 },
        { wch: 25 }, { wch: 20 }, { wch: 50 },
      ];
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Tickets');
      XLSX.writeFile(wb, `NABL_Support_Tickets_${new Date().toISOString().slice(0, 10)}.xlsx`);
      showSnackbar(`Exported ${rows.length} tickets`, 'success');
    } catch (e) { showSnackbar('Export failed: ' + (e.response?.data?.message || e.message), 'error'); }
    finally { setExporting(false); }
  };

  const [aiOpen, setAiOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try { const r = await nablSupportService.getStats(); if (r.data?.success) setStats(r.data.data || {}); }
    catch (e) { console.error(e); } finally { setStatsLoading(false); }
  }, []);

  const fetchDevTickets = useCallback(async (pg = 1, s = search) => {
    setDevLoading(true);
    try {
      const r = await nablSupportService.getTickets({ page: pg, pageSize, search: s, ticketType: 'Development', excludeClosed: true });
      if (r.data?.success) { setDevTickets(r.data.data || []); setDevTotal(r.data.total || 0); setDevTotalPages(r.data.totalPages || 1); setDevPage(pg); }
    } catch (e) { showSnackbar('Failed to load development tickets', 'error'); }
    finally { setDevLoading(false); }
  }, [pageSize, search]);

  const fetchSupTickets = useCallback(async (pg = 1, s = search) => {
    setSupLoading(true);
    try {
      const r = await nablSupportService.getTickets({ page: pg, pageSize, search: s, ticketType: 'Support', excludeClosed: true });
      if (r.data?.success) { setSupTickets(r.data.data || []); setSupTotal(r.data.total || 0); setSupTotalPages(r.data.totalPages || 1); setSupPage(pg); }
    } catch (e) { showSnackbar('Failed to load support tickets', 'error'); }
    finally { setSupLoading(false); }
  }, [pageSize, search]);

  const fetchClosedTickets = useCallback(async (pg = 1, s = search) => {
    setClosedLoading(true);
    try {
      const r = await nablSupportService.getTickets({ page: pg, pageSize, search: s, status: 'Closed' });
      if (r.data?.success) { setClosedTickets(r.data.data || []); setClosedTotal(r.data.total || 0); setClosedTotalPages(r.data.totalPages || 1); setClosedPage(pg); }
    } catch (e) { showSnackbar('Failed to load closed tickets', 'error'); }
    finally { setClosedLoading(false); }
  }, [pageSize, search]);

  const fetchAssignments = useCallback(async () => {
    try { const r = await nablSupportService.getAllAssignments(); if (r.data?.success) setAssignmentsMap(r.data.data || {}); }
    catch (e) { console.error(e); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try { const r = await nablSupportService.getUsers(); if (r.data?.success) setAllUsers(r.data.data || []); }
    catch (e) { console.error(e); }
  }, []);

  const fetchAll = useCallback((s = search) => {
    fetchStats(); fetchDevTickets(1, s); fetchSupTickets(1, s); fetchClosedTickets(1, s); fetchAssignments();
  }, [fetchStats, fetchDevTickets, fetchSupTickets, fetchClosedTickets, fetchAssignments, search]);

  useEffect(() => { fetchAll(); fetchUsers(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await nablSupportService.syncTickets();
      showSnackbar(r.data?.message || 'Sync complete', 'success');
      fetchAll(search);
    } catch (e) { showSnackbar('Sync failed: ' + (e.response?.data?.message || e.message), 'error'); }
    finally { setSyncing(false); }
  };

  const handleStatusAction = async (ticketNumber, newStatus) => {
    setStatusActionLoading(ticketNumber);
    try {
      await nablSupportService.updateTicketStatus(ticketNumber, newStatus);
      showSnackbar(`Ticket ${ticketNumber} → ${newStatus}`, 'success');
      fetchAll(search);
    } catch (e) { showSnackbar('Update failed: ' + (e.response?.data?.message || e.message), 'error'); }
    finally { setStatusActionLoading(null); }
  };

  const handleTypeAction = async (ticketNumber, newType) => {
    setTypeActionLoading(ticketNumber);
    try {
      await nablSupportService.updateTicketType(ticketNumber, newType);
      showSnackbar(`Ticket ${ticketNumber} → ${newType}`, 'success');
      fetchAll(search);
    } catch (e) { showSnackbar('Type update failed', 'error'); }
    finally { setTypeActionLoading(null); }
  };

  const handleOpenAssign = (ticket) => {
    setAssignTarget(ticket);
    setSelectedUserIds((assignmentsMap[ticket.ticketNumber] || []).map(u => u.user_id));
    setAssignDialog(true);
  };

  const handleAssignSubmit = async () => {
    if (!assignTarget) return;
    setAssigning(true);
    try {
      await nablSupportService.assignTicket(assignTarget.ticketNumber, selectedUserIds);
      showSnackbar(`Assigned to ${selectedUserIds.length} user(s)`, 'success');
      setAssignDialog(false);
      fetchAssignments();
    } catch (e) { showSnackbar('Assignment failed', 'error'); }
    finally { setAssigning(false); }
  };

  const handleRowClick = async (ticket) => {
    setSelectedTicket(ticket);
    setDrawerOpen(true);
    setThreads([]);
    setThreadsLoading(true);
    try {
      const r = await nablSupportService.getTicketThreads(ticket.ticketNumber);
      if (r.data?.success) setThreads(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setThreadsLoading(false); }
  };

  const commonProps = {
    pageSize, onStatusAction: handleStatusAction, onTypeAction: handleTypeAction,
    statusActionLoading, typeActionLoading, assignmentsMap,
    onAssign: handleOpenAssign, onRowClick: handleRowClick,
  };

  return (
    <Box sx={{ p: 2, maxWidth: '100%' }}>
      {/* Header */}
      <Paper elevation={0} sx={{ background: 'linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)', color: '#fff', borderRadius: 2, mb: 2, p: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TicketIcon sx={{ fontSize: 36 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>NABL Support Tickets</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Zoho Desk — Last synced: {statsLoading ? '...' : stats.lastSyncTime ? new Date(stats.lastSyncTime).toLocaleString('en-IN') : 'Never'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchAll(search)} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>Refresh</Button>
            <Button
              size="small" variant="outlined"
              startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
              onClick={handleExportExcel} disabled={exporting}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              {exporting ? 'Exporting…' : 'Download Excel'}
            </Button>
            <Button size="small" variant="contained" startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />} onClick={handleSync} disabled={syncing} sx={{ bgcolor: '#E34124', '&:hover': { bgcolor: '#c2341c' } }}>
              {syncing ? 'Syncing…' : 'Sync from Zoho'}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Stat Cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Open', value: stats.openTickets, icon: <HourglassEmptyIcon />, color: '#d32f2f' },
          { label: 'Development', value: devTotal, icon: <DevIcon />, color: '#7b1fa2' },
          { label: 'Support', value: supTotal, icon: <SupportIcon />, color: '#0288d1' },
        ].map(c => (
          <Grid key={c.label} item xs={6} sm={4} md={4}>
            <StatCard {...c} />
          </Grid>
        ))}
      </Grid>

      {/* Search */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search ticket no, email, subject…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchAll(search)}
            sx={{ flex: 1, minWidth: 200 }} />
          <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={() => fetchAll(search)}>Search</Button>
          <Button size="small" variant="outlined" onClick={() => { setSearch(''); fetchAll(''); }}>Reset</Button>
        </Box>
      </Paper>

      {/* Dev + Support side by side */}
      <Grid container spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
        <Grid item xs={12} lg={6}>
          <TicketTable
            title="💻 Development Tickets" headerColor="#7b1fa2"
            tickets={devTickets} loading={devLoading} total={devTotal}
            page={devPage} totalPages={devTotalPages}
            onPageChange={(pg) => fetchDevTickets(pg, search)}
            {...commonProps}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <TicketTable
            title="🎧 Support Tickets" headerColor="#0288d1"
            tickets={supTickets} loading={supLoading} total={supTotal}
            page={supPage} totalPages={supTotalPages}
            onPageChange={(pg) => fetchSupTickets(pg, search)}
            {...commonProps}
          />
        </Grid>
      </Grid>

      {/* Closed tickets — full width */}
      <TicketTable
        title="✅ Closed Tickets" headerColor="#2e7d32"
        tickets={closedTickets} loading={closedLoading} total={closedTotal}
        page={closedPage} totalPages={closedTotalPages}
        onPageChange={(pg) => fetchClosedTickets(pg, search)}
        {...commonProps}
      />

      {/* Assign Dialog */}
      <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Assign #{assignTarget?.ticketNumber}</Typography>
            <IconButton size="small" onClick={() => setAssignDialog(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Select one or more users:</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {allUsers.map((u, i) => (
              <Paper key={u.id} variant="outlined"
                sx={{ px: 1.5, py: 0.75, cursor: 'pointer', bgcolor: selectedUserIds.includes(u.id) ? '#e3f2fd' : '#fff', borderColor: selectedUserIds.includes(u.id) ? '#1976d2' : '#e0e0e0' }}
                onClick={() => setSelectedUserIds(prev => prev.includes(u.id) ? prev.filter(id => id !== u.id) : [...prev, u.id])}>
                <FormControlLabel
                  control={<Checkbox checked={selectedUserIds.includes(u.id)} color="primary" size="small" onChange={() => {}} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.68rem', bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{u.name[0].toUpperCase()}</Avatar>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{u.name}</Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%', pointerEvents: 'none' }}
                />
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button onClick={() => setAssignDialog(false)} variant="outlined" size="small">Cancel</Button>
          <Button onClick={handleAssignSubmit} variant="contained" size="small" disabled={assigning}
            startIcon={assigning ? <CircularProgress size={14} color="inherit" /> : <AssignIcon />}>
            {assigning ? 'Assigning…' : `Assign (${selectedUserIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Thread Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#00024c', color: '#fff' }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Ticket #{selectedTicket?.ticketNumber}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>{selectedTicket?.subject}</Typography>
          </Box>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ p: 2 }}>
          {selectedTicket && (
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: '#f8fafc' }}>
              <Grid container spacing={1}>
                <Grid item xs={7}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{selectedTicket.email || selectedTicket.fromEmailAddress || '—'}</Typography>
                </Grid>
                <Grid item xs={2.5}>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box><Chip label={selectedTicket.status} size="small" color={STATUS_COLOR[selectedTicket.status] || 'default'} sx={{ height: 18, fontSize: '0.62rem' }} /></Box>
                </Grid>
                <Grid item xs={2.5}>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Box><Chip label={selectedTicket.ticket_type || 'Support'} size="small" color={selectedTicket.ticket_type === 'Development' ? 'secondary' : 'primary'} sx={{ height: 18, fontSize: '0.62rem' }} /></Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                    {(assignmentsMap[selectedTicket.ticketNumber] || []).map((u, i) => (
                      <Chip key={u.user_id} label={u.name} size="small" avatar={<Avatar sx={{ bgcolor: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>{u.name[0]}</Avatar>} />
                    ))}
                    {!(assignmentsMap[selectedTicket.ticketNumber] || []).length && <Typography variant="caption" color="text.disabled">Unassigned</Typography>}
                  </Box>
                </Grid>
                {selectedTicket.webUrl && (
                  <Grid item xs={12}>
                    <Button size="small" startIcon={<OpenInNewIcon />} href={selectedTicket.webUrl} target="_blank" rel="noreferrer">Open in Zoho Desk</Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}

          {/* Action buttons in drawer */}
          {selectedTicket && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {/* Close / Reopen */}
              {(() => {
                const isOpen = selectedTicket.status === 'Open' || selectedTicket.status === 'On Hold';
                return (
                  <Button
                    size="small"
                    variant="contained"
                    color={isOpen ? 'success' : 'error'}
                    startIcon={statusActionLoading === selectedTicket.ticketNumber
                      ? <CircularProgress size={14} color="inherit" />
                      : isOpen ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                    disabled={statusActionLoading === selectedTicket.ticketNumber}
                    onClick={async () => {
                      await handleStatusAction(selectedTicket.ticketNumber, isOpen ? 'Closed' : 'Open');
                      setSelectedTicket(prev => ({ ...prev, status: isOpen ? 'Closed' : 'Open' }));
                    }}
                  >
                    {statusActionLoading === selectedTicket.ticketNumber ? 'Updating…' : isOpen ? 'Close Ticket' : 'Reopen Ticket'}
                  </Button>
                );
              })()}

              {/* Assign */}
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<AssignIcon fontSize="small" />}
                onClick={() => handleOpenAssign(selectedTicket)}
              >
                Assign
              </Button>

              {/* Type toggle */}
              {(() => {
                const isDev = selectedTicket.ticket_type === 'Development';
                return (
                  <Button
                    size="small"
                    variant="outlined"
                    color={isDev ? 'warning' : 'secondary'}
                    startIcon={typeActionLoading === selectedTicket.ticketNumber
                      ? <CircularProgress size={14} color="inherit" />
                      : isDev ? <SupportIcon fontSize="small" /> : <DevIcon fontSize="small" />}
                    disabled={typeActionLoading === selectedTicket.ticketNumber}
                    onClick={async () => {
                      const newType = isDev ? 'Support' : 'Development';
                      await handleTypeAction(selectedTicket.ticketNumber, newType);
                      setSelectedTicket(prev => ({ ...prev, ticket_type: newType }));
                    }}
                  >
                    {typeActionLoading === selectedTicket.ticketNumber ? '…' : isDev ? 'Move to Support' : 'Mark as Dev'}
                  </Button>
                );
              })()}
            </Box>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Threads ({threads.length})</Typography>
          <Divider sx={{ mb: 1.5 }} />

          {threadsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : threads.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>No threads found</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {threads.map((th, i) => (
                <Paper key={th.id || i} elevation={1} sx={{ p: 1.5, borderLeft: `3px solid ${th.direction === 'in' ? '#1976d2' : '#2e7d32'}`, bgcolor: th.isDescriptionThread === 'true' ? '#fffde7' : '#fff' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <EmailIcon sx={{ fontSize: 14, color: th.direction === 'in' ? '#1976d2' : '#2e7d32' }} />
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{th.fromEmailAddress || '—'}</Typography>
                      {th.isDescriptionThread === 'true' && <Chip label="Desc" size="small" color="warning" sx={{ height: 16, fontSize: '0.6rem' }} />}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip label={th.direction || 'N/A'} size="small" color={th.direction === 'in' ? 'primary' : 'success'} variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
                      {th.channel && <Chip label={th.channel} size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />}
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mb: 0.75 }}>
                    {th.createdTime ? new Date(th.createdTime).toLocaleString('en-IN') : ''}{th.to && ` → ${th.to}`}
                  </Typography>
                  {th.content
                    ? <Box sx={{ fontSize: '0.78rem', maxHeight: 180, overflowY: 'auto', bgcolor: '#f8fafc', p: 1, borderRadius: 1 }} dangerouslySetInnerHTML={{ __html: th.content }} />
                    : th.summary
                      ? <Typography sx={{ fontSize: '0.75rem', fontStyle: 'italic', color: 'text.secondary' }}>{th.summary}</Typography>
                      : null}
                  {parseInt(th.attachmentCount) > 0 && (
                    <Typography sx={{ fontSize: '0.7rem', color: '#1976d2', mt: 0.5 }}>📎 {th.attachmentCount} attachment(s)</Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Drawer>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>

      {/* AI FAB */}
      <Tooltip title="AI Assistant (Perplexity)" placement="left">
        <Fab
          onClick={() => setAiOpen(true)}
          sx={{
            position: 'fixed', bottom: 28, right: 28, zIndex: 1300,
            background: 'linear-gradient(135deg, #7b1fa2, #1976d2)',
            color: '#fff',
            '&:hover': { background: 'linear-gradient(135deg, #6a1b9a, #1565c0)' },
          }}
        >
          <AiIcon />
        </Fab>
      </Tooltip>

      {/* AI Drawer — Perplexity iframe */}
      <Drawer
        anchor="right"
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ px: 2, py: 1.25, background: 'linear-gradient(135deg, #7b1fa2, #1976d2)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AiIcon />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>AI Assistant</Typography>
          </Box>
          <IconButton onClick={() => setAiOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <iframe
            src="https://huggingface.co/chat"
            title="HuggingChat AI"
            width="100%"
            height="100%"
            style={{ border: 'none', display: 'block', height: '100%' }}
            allow="microphone"
          />
        </Box>
      </Drawer>
    </Box>
  );
}
